import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, PencilSimple, ChartPie, ChartBar } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth, can } from "@/context/AuthContext";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";

const fmtEUR = (n) => (n || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

// Cálculo de deuda pendiente de un socio, en base a partial_payments y importe_total
const deudaSocio = (m) => {
  const total = Number(m?.importe_total || 0);
  const pagado = (m?.partial_payments || []).reduce((s, p) => s + Number(p.importe || 0), 0);
  return Math.max(0, total - pagado);
};
const pagadoSocio = (m) => (m?.partial_payments || []).reduce((s, p) => s + Number(p.importe || 0), 0);

export default function Cuotas() {
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [members, setMembers] = useState([]);
  const [filterEstado, setFilterEstado] = useState("");
  const [filterTemporada, setFilterTemporada] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    member_id: "", tipo: "anual", importe: 60, temporada: "2026-27", estado: "pagado",
    fecha: "", observaciones: "",
  });
  const canPay = can(user, "admin", "taquilla", "tesoreria");
  const canEditPay = can(user, "admin", "tesoreria");

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filterEstado) params.estado = filterEstado;
      if (filterTemporada) params.temporada = filterTemporada;
      const [p, m] = await Promise.all([
        api.get("/payments", { params }),
        api.get("/members"),
      ]);
      setPayments(p.data);
      setMembers(m.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, [filterEstado, filterTemporada]);

  useEffect(() => { load(); }, [load]);

  const memberOf = (id) => members.find((m) => m.id === id);

  // Temporadas disponibles (socios + pagos)
  const temporadas = useMemo(() => {
    const set = new Set();
    members.forEach((m) => m.temporada && set.add(m.temporada));
    payments.forEach((p) => p.temporada && set.add(p.temporada));
    return Array.from(set).sort().reverse();
  }, [members, payments]);

  // Socios visibles según filtro de temporada (para gráficos y totales)
  const sociosBase = useMemo(() => {
    return members.filter((m) => m.estado !== "baja" && (!filterTemporada || m.temporada === filterTemporada));
  }, [members, filterTemporada]);

  // Totales globales sobre socios (para pie + bar)
  const globalTotals = useMemo(() => {
    let cobrado = 0, previsto = 0;
    sociosBase.forEach((m) => {
      cobrado += pagadoSocio(m);
      previsto += Number(m.importe_total || 0);
    });
    const pendiente = Math.max(0, previsto - cobrado);
    return { cobrado, pendiente, previsto };
  }, [sociosBase]);

  // Datos para gráfico de columnas por temporada
  const bySeasonData = useMemo(() => {
    const map = {};
    members.filter((m) => m.estado !== "baja").forEach((m) => {
      const t = m.temporada || "—";
      if (!map[t]) map[t] = { temporada: t, cobrado: 0, pendiente: 0 };
      map[t].cobrado += pagadoSocio(m);
      map[t].pendiente += Math.max(0, Number(m.importe_total || 0) - pagadoSocio(m));
    });
    return Object.values(map).sort((a, b) => a.temporada.localeCompare(b.temporada));
  }, [members]);

  const pieData = [
    { name: "Cobrado", value: Math.round(globalTotals.cobrado), color: "#22C55E" },
    { name: "Pendiente", value: Math.round(globalTotals.pendiente), color: "#F97316" },
  ].filter((d) => d.value > 0);
  const pieTotal = pieData.reduce((s, d) => s + d.value, 0);

  // Totales de la tabla de pagos
  const totales = useMemo(() => {
    let importe = 0, pagado = 0, pendiente = 0;
    payments.forEach((p) => {
      const m = memberOf(p.member_id);
      importe += Number(p.importe || 0);
      if (p.estado === "pagado") pagado += Number(p.importe || 0);
      if (m) pendiente += deudaSocio(m);
    });
    return { importe, pagado, pendiente };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payments, members]);

  const openNew = () => {
    setEditing(null);
    setForm({ member_id: "", tipo: "anual", importe: 60, temporada: filterTemporada || "2026-27", estado: "pagado", fecha: "", observaciones: "" });
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      member_id: p.member_id, tipo: p.tipo, importe: p.importe,
      temporada: p.temporada, estado: p.estado,
      fecha: (p.fecha || "").slice(0, 10),
      observaciones: p.observaciones || "",
    });
    setShowForm(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const payload = { ...form };
        // No se permite cambiar socio en edición
        delete payload.member_id;
        await api.put(`/payments/${editing.id}`, payload);
        toast.success("Pago modificado");
      } else {
        await api.post("/payments", form);
        toast.success("Pago registrado");
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const socMap = useMemo(() => {
    const map = {};
    members.forEach((m) => { map[m.id] = m; });
    return map;
  }, [members]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#007AFF] font-bold">Módulo 2</div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tighter">Cuotas</h1>
          <p className="text-sm text-slate-500 mt-1">Gestión de pagos y cuotas de socios por temporada</p>
        </div>
        {canPay && (
          <button
            data-testid="add-pago-btn"
            onClick={openNew}
            className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-5 h-11 flex items-center gap-2 font-display font-bold uppercase tracking-wide text-sm active:scale-[0.98] shadow-md shadow-blue-500/20"
            style={{ borderRadius: 8 }}
          >
            <Plus size={18} weight="bold" /> Registrar Pago
          </button>
        )}
      </div>

      {/* Summary global (basado en socios) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SumCard label="Previsto" value={fmtEUR(globalTotals.previsto)} />
        <SumCard label="Cobrado" value={fmtEUR(globalTotals.cobrado)} color="text-[#16a34a]" />
        <SumCard label="Pendiente" value={fmtEUR(globalTotals.pendiente)} color="text-[#F97316]" />
        <SumCard
          label="% Cobrado"
          value={globalTotals.previsto ? Math.round(globalTotals.cobrado / globalTotals.previsto * 100) + "%" : "—"}
          color="text-[#2563EB]"
        />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card title="% Cobrado vs Pendiente" icon={ChartPie}>
          {pieTotal === 0 ? (
            <div className="text-center text-slate-500 text-sm py-16">Sin datos económicos</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={pieData} dataKey="value" innerRadius={50} outerRadius={90} paddingAngle={2} stroke="none" label={(e) => `${Math.round(e.value / pieTotal * 100)}%`}>
                    {pieData.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 6, color: "#0F172A", fontSize: 12 }} formatter={(v) => fmtEUR(v)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card title="Cobrado vs Pendiente por temporada" icon={ChartBar}>
          {bySeasonData.length === 0 ? (
            <div className="text-center text-slate-500 text-sm py-16">Sin temporadas</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer>
                <BarChart data={bySeasonData} margin={{ left: -10, right: 8, top: 8, bottom: 0 }}>
                  <CartesianGrid stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="temporada" axisLine={false} tickLine={false} />
                  <YAxis axisLine={false} tickLine={false} />
                  <Tooltip formatter={(v) => fmtEUR(v)} contentStyle={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 6, color: "#0F172A", fontSize: 12 }} />
                  <Legend />
                  <Bar dataKey="cobrado" name="Cobrado" fill="#22C55E" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="pendiente" name="Pendiente" fill="#F97316" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select
          data-testid="cuotas-filter-temporada"
          value={filterTemporada}
          onChange={(e) => setFilterTemporada(e.target.value)}
          className="bg-white border border-slate-200 px-4 h-11 text-slate-900 focus:border-[#2563EB] outline-none"
        >
          <option value="">Todas las temporadas</option>
          {temporadas.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select
          data-testid="cuotas-filter-estado"
          value={filterEstado}
          onChange={(e) => setFilterEstado(e.target.value)}
          className="bg-white border border-slate-200 px-4 h-11 text-slate-900 focus:border-[#2563EB] outline-none"
        >
          <option value="">Todos los pagos</option>
          <option value="pagado">Pagados</option>
          <option value="pendiente">Pendientes</option>
          <option value="parcial">Parciales</option>
        </select>
      </div>

      {/* Payments table */}
      <div className="bg-white border border-slate-200 overflow-x-auto" style={{ borderRadius: 6 }}>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left">
              <Th>Socio</Th>
              <Th>Tipo</Th>
              <Th>Temporada</Th>
              <Th>Fecha</Th>
              <Th>Estado</Th>
              <Th className="text-right">Importe</Th>
              <Th className="text-right" title="Deuda pendiente del socio">Importe Parcial</Th>
              {canEditPay && <Th className="text-right"></Th>}
            </tr>
          </thead>
          <tbody>
            {payments.length === 0 && (
              <tr><td colSpan={8} className="text-center py-12 text-slate-500">Sin pagos registrados</td></tr>
            )}
            {payments.map((p) => {
              const m = socMap[p.member_id];
              const parcial = m ? deudaSocio(m) : 0;
              return (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`pago-row-${p.id}`}>
                  <td className="px-4 py-3">
                    {m ? <>#{m.numero_socio} {m.nombre} {m.apellidos}</> : <span className="text-slate-500">—</span>}
                  </td>
                  <td className="px-4 py-3 capitalize">{p.tipo}</td>
                  <td className="px-4 py-3">{p.temporada}</td>
                  <td className="px-4 py-3 text-slate-600">{p.fecha?.slice(0, 10)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-widest font-bold ${p.estado === "pagado" ? "text-[#22C55E]" : p.estado === "pendiente" ? "text-[#F97316]" : "text-[#3B82F6]"}`}>
                      {p.estado}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-display font-bold text-lg">{fmtEUR(p.importe)}</td>
                  <td className="px-4 py-3 text-right font-bold" style={{ color: parcial > 0 ? "#F97316" : "#22C55E" }}>
                    {fmtEUR(parcial)}
                  </td>
                  {canEditPay && (
                    <td className="px-4 py-3 text-right">
                      <button
                        data-testid={`pago-edit-${p.id}`}
                        onClick={() => openEdit(p)}
                        className="h-8 px-3 inline-flex items-center gap-1 border border-slate-200 hover:border-[#2563EB] hover:text-[#2563EB] text-xs font-bold uppercase tracking-wider"
                        style={{ borderRadius: 6 }}
                      >
                        <PencilSimple size={14} /> Modificar
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          {payments.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                <td className="px-4 py-3 uppercase text-[10px] tracking-widest text-slate-700" colSpan={5}>Totales</td>
                <td className="px-4 py-3 text-right font-display font-bold text-lg text-[#16a34a]" data-testid="cuotas-total-importe">{fmtEUR(totales.importe)}</td>
                <td className="px-4 py-3 text-right font-display font-bold text-lg text-[#F97316]" data-testid="cuotas-total-parcial">{fmtEUR(totales.pendiente)}</td>
                {canEditPay && <td></td>}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={onSubmit} className="bg-white border border-slate-200 w-full max-w-lg p-6 space-y-4" style={{ borderRadius: 12 }}>
            <h2 className="font-display font-bold text-2xl uppercase">{editing ? "Modificar Pago" : "Registrar Pago"}</h2>
            <Field label="Socio" required>
              <select data-testid="pago-socio" required disabled={!!editing} value={form.member_id} onChange={(e) => setForm({ ...form, member_id: e.target.value })} className={inputCls}>
                <option value="">— Selecciona —</option>
                {members.filter((m) => m.estado !== "baja").map((m) => (
                  <option key={m.id} value={m.id}>#{m.numero_socio} - {m.nombre} {m.apellidos}</option>
                ))}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Tipo">
                <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} className={inputCls}>
                  <option value="anual">Anual</option>
                  <option value="semestral">Semestral</option>
                  <option value="especial">Especial</option>
                  <option value="renovacion">Renovación</option>
                </select>
              </Field>
              <Field label="Importe €" required>
                <input data-testid="pago-importe" type="number" step="0.01" required value={form.importe} onChange={(e) => setForm({ ...form, importe: parseFloat(e.target.value) || 0 })} className={inputCls} />
              </Field>
              <Field label="Temporada" required>
                <input required value={form.temporada} onChange={(e) => setForm({ ...form, temporada: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Estado">
                <select value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value })} className={inputCls}>
                  <option value="pagado">Pagado</option>
                  <option value="pendiente">Pendiente</option>
                  <option value="parcial">Parcial</option>
                </select>
              </Field>
              <Field label="Fecha">
                <input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} className={inputCls} />
              </Field>
            </div>
            <Field label="Observaciones">
              <textarea rows={2} value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} className={inputCls} />
            </Field>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="px-5 h-11 border border-slate-200" style={{ borderRadius: 6 }}>Cancelar</button>
              <button data-testid="pago-submit" type="submit" className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-5 h-11 font-display font-bold uppercase tracking-wide" style={{ borderRadius: 6 }}>
                {editing ? "Guardar cambios" : "Registrar"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full bg-slate-50 border border-slate-200 px-3 h-10 text-slate-900 focus:border-[#2563EB] outline-none placeholder:text-slate-400";

function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 text-[10px] uppercase tracking-widest text-slate-500 font-bold ${className}`}>{children}</th>;
}

function SumCard({ label, value, color = "text-slate-900" }) {
  return (
    <div className="bg-white border border-slate-200 p-4 shadow-card" style={{ borderRadius: 12 }}>
      <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold">{label}</div>
      <div className={`font-display font-bold text-2xl mt-1 tracking-tighter ${color}`}>{value}</div>
    </div>
  );
}

function Card({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-slate-200 p-5 shadow-card" style={{ borderRadius: 12 }}>
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon size={18} className="text-[#2563EB]" weight="bold" />}
        <h3 className="font-display font-bold text-xl uppercase tracking-tight">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.2em] uppercase font-bold text-slate-600 mb-1.5">
        {label}{required && <span className="text-[#EF4444]"> *</span>}
      </span>
      {children}
    </label>
  );
}
